/**
 * Event utility functions for formatting dates, times, and calendar integration
 */

import { siteConfig } from "@/lib/site-config";

const DEFAULT_EVENT_LOCATION = `${siteConfig.name}, ${siteConfig.address.line1}, ${siteConfig.address.city}, ${siteConfig.address.state} ${siteConfig.address.zip}`;

/**
 * Format event date for display
 */
export function formatEventDate(dateString: string): {
  full: string;
  short: string;
  day: string;
  month: string;
  year: string;
  dayOfWeek: string;
} {
  // Parse as local timezone (not UTC) to avoid date shifting
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return {
    full: date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    short: date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    day: date.getDate().toString(),
    month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
    year: date.getFullYear().toString(),
    dayOfWeek: date.toLocaleDateString("en-US", { weekday: "long" }),
  };
}

/**
 * Convert 24-hour time string to 12-hour format
 */
export function formatTimeTo12Hour(timeString: string): string {
  const [hours, minutes] = timeString.split(":").map(Number);

  if (isNaN(hours) || isNaN(minutes)) {
    return timeString;
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const displayMinutes = minutes.toString().padStart(2, "0");

  return `${displayHours}:${displayMinutes} ${period}`;
}

/**
 * Get event type configuration (icon, label, color)
 */
export function getEventTypeConfig(eventType: string): {
  label: string;
  color: string;
  bgColor: string;
} {
  switch (eventType) {
    case "live_music":
      return {
        label: "Live Music",
        color: "text-amber-700 dark:text-amber-300",
        bgColor: "bg-amber-100 dark:bg-amber-900",
      };
    case "special_dinner":
      return {
        label: "Special Dinner",
        color: "text-rose-700 dark:text-rose-300",
        bgColor: "bg-rose-100 dark:bg-rose-900",
      };
    case "trivia":
      return {
        label: "Trivia Night",
        color: "text-cyan-700 dark:text-cyan-300",
        bgColor: "bg-cyan-100 dark:bg-cyan-900",
      };
    case "brunch":
      return {
        label: "Brunch",
        color: "text-orange-700 dark:text-orange-300",
        bgColor: "bg-orange-100 dark:bg-orange-900",
      };
    case "taco_tuesday":
      return {
        label: "Taco Tuesday",
        color: "text-lime-700 dark:text-lime-300",
        bgColor: "bg-lime-100 dark:bg-lime-900",
      };
    case "watch_party":
      return {
        label: "Watch Party",
        color: "text-sky-700 dark:text-sky-300",
        bgColor: "bg-sky-100 dark:bg-sky-900",
      };
    case "happy_hour":
      return {
        label: "Happy Hour",
        color: "text-teal-700 dark:text-teal-300",
        bgColor: "bg-teal-100 dark:bg-teal-900",
      };
    case "holiday":
      return {
        label: "Holiday Event",
        color: "text-red-700 dark:text-red-300",
        bgColor: "bg-red-100 dark:bg-red-900",
      };
    case "community":
      return {
        label: "Community Event",
        color: "text-indigo-700 dark:text-indigo-300",
        bgColor: "bg-indigo-100 dark:bg-indigo-900",
      };
    default:
      return {
        label: "Event",
        color: "text-zinc-700 dark:text-zinc-300",
        bgColor: "bg-zinc-100 dark:bg-zinc-900",
      };
  }
}

/**
 * Generate Google Calendar URL for event
 */
export function generateGoogleCalendarUrl(event: {
  title: string;
  description: string;
  date: string;
  startTime: string;
  endTime?: string;
  location?: string;
}): string {
  // Parse date as local timezone (not UTC)
  const [year, month, day] = event.date.split("-").map(Number);
  const eventDate = new Date(year, month - 1, day);
  const [startHours, startMinutes] = event.startTime.split(":").map(Number);

  const startDateTime = new Date(eventDate);
  startDateTime.setHours(startHours, startMinutes, 0, 0);

  let endDateTime: Date;
  if (event.endTime) {
    const [endHours, endMinutes] = event.endTime.split(":").map(Number);
    endDateTime = new Date(eventDate);
    endDateTime.setHours(endHours, endMinutes, 0, 0);
  } else {
    // Default to 2 hours after start
    endDateTime = new Date(startDateTime);
    endDateTime.setHours(endDateTime.getHours() + 2);
  }

  // Format for Google Calendar (YYYYMMDDTHHMMSS) - local time without Z
  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  };

  const location = event.location || DEFAULT_EVENT_LOCATION;

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${formatLocalDate(startDateTime)}/${formatLocalDate(endDateTime)}`,
    details: event.description,
    location: location,
    ctz: "America/Los_Angeles",
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate Outlook.com calendar URL for event
 */
export function generateOutlookCalendarUrl(event: {
  title: string;
  description: string;
  date: string;
  startTime: string;
  endTime?: string;
  location?: string;
}): string {
  // Parse date as local timezone (not UTC)
  const [year, month, day] = event.date.split("-").map(Number);
  const eventDate = new Date(year, month - 1, day);
  const [startHours, startMinutes] = event.startTime.split(":").map(Number);

  const startDateTime = new Date(eventDate);
  startDateTime.setHours(startHours, startMinutes, 0, 0);

  let endDateTime: Date;
  if (event.endTime) {
    const [endHours, endMinutes] = event.endTime.split(":").map(Number);
    endDateTime = new Date(eventDate);
    endDateTime.setHours(endHours, endMinutes, 0, 0);
  } else {
    endDateTime = new Date(startDateTime);
    endDateTime.setHours(endDateTime.getHours() + 2);
  }

  const location = event.location || DEFAULT_EVENT_LOCATION;

  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    body: event.description,
    startdt: startDateTime.toISOString(),
    enddt: endDateTime.toISOString(),
    location: location,
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Generate iCal file content for download
 */
export function generateICalContent(event: {
  title: string;
  description: string;
  date: string;
  startTime: string;
  endTime?: string;
  location?: string;
}): string {
  const startDateTime = new Date(event.date + "T" + event.startTime + ":00");
  let endDateTime: Date;

  if (event.endTime) {
    endDateTime = new Date(event.date + "T" + event.endTime + ":00");
  } else {
    endDateTime = new Date(startDateTime);
    endDateTime.setHours(endDateTime.getHours() + 2);
  }

  const formatICalDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  };

  const location = event.location || DEFAULT_EVENT_LOCATION;

  // Escape special characters for iCal format
  const escapeICalText = (text: string) => {
    return text
      .replace(/\\/g, "\\\\")
      .replace(/;/g, "\\;")
      .replace(/,/g, "\\,")
      .replace(/\n/g, "\\n");
  };

  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Tuscan Cove Bar & Patio//Events//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${Date.now()}@tuscancove.com
DTSTAMP:${formatICalDate(new Date())}
DTSTART:${formatICalDate(startDateTime)}
DTEND:${formatICalDate(endDateTime)}
SUMMARY:${escapeICalText(event.title)}
DESCRIPTION:${escapeICalText(event.description)}
LOCATION:${escapeICalText(location)}
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;
}

/**
 * Download iCal file
 */
export function downloadICalFile(event: {
  title: string;
  description: string;
  date: string;
  startTime: string;
  endTime?: string;
  location?: string;
  slug?: string;
}): void {
  const content = generateICalContent(event);
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = `${event.slug || "event"}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
